// Accessibility utilities and hooks

import React, { useEffect, useRef } from 'react';

/**
 * Focus management utilities
 */

// Hook to manage focus trap for modals and dialogs
export function useFocusTrap(isActive: boolean) {
  const containerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const closeButton = container.querySelector('[data-close-button]') as HTMLElement;
        if (closeButton) {
          closeButton.click();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscapeKey);

    // Focus first element when modal opens
    if (firstElement) {
      firstElement.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isActive]);

  return containerRef;
}

// Hook to manage focus restoration
export function useFocusRestore() {
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  const storeFocus = () => {
    lastFocusedElement.current = document.activeElement as HTMLElement;
  };

  const restoreFocus = () => {
    if (lastFocusedElement.current) {
      lastFocusedElement.current.focus();
    }
  };

  return { storeFocus, restoreFocus };
}

// Hook for auto-focus on component mount
export function useAutoFocus(shouldFocus: boolean = true) {
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (shouldFocus && elementRef.current) {
      elementRef.current.focus();
    }
  }, [shouldFocus]);

  return elementRef;
}

/**
 * Keyboard navigation utilities
 */

// Hook for arrow key navigation in lists
export function useArrowKeyNavigation(itemCount: number, onActivate?: (index: number) => void) {
  const activeIndex = useRef(0);
  const containerRef = useRef<HTMLElement>(null);

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        activeIndex.current = Math.min(activeIndex.current + 1, itemCount - 1);
        updateFocus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        activeIndex.current = Math.max(activeIndex.current - 1, 0);
        updateFocus();
        break;
      case 'Home':
        e.preventDefault();
        activeIndex.current = 0;
        updateFocus();
        break;
      case 'End':
        e.preventDefault();
        activeIndex.current = itemCount - 1;
        updateFocus();
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (onActivate) {
          onActivate(activeIndex.current);
        }
        break;
    }
  };

  const updateFocus = () => {
    if (containerRef.current) {
      const items = containerRef.current.querySelectorAll('[role="option"], [role="menuitem"], [data-nav-item]');
      const activeItem = items[activeIndex.current] as HTMLElement;
      if (activeItem) {
        activeItem.focus();
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => container.removeEventListener('keydown', handleKeyDown);
    }
  }, [itemCount, onActivate]);

  return { containerRef, activeIndex: activeIndex.current };
}

/**
 * ARIA utilities
 */

// Generate unique IDs for ARIA relationships
export function useAriaId(prefix: string = 'aria-id') {
  const id = useRef(`${prefix}-${Math.random().toString(36).substr(2, 9)}`);
  return id.current;
}

// Hook for managing ARIA live regions
export function useAriaLiveRegion() {
  const liveRegionRef = useRef<HTMLDivElement>(null);

  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (liveRegionRef.current) {
      liveRegionRef.current.setAttribute('aria-live', priority);
      liveRegionRef.current.textContent = message;
      
      // Clear after announcement
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = '';
        }
      }, 1000);
    }
  };

  const LiveRegion = () => (
    <div
      ref={liveRegionRef}
      className="sr-only"
      aria-live="polite"
      aria-atomic="true"
    />
  );

  return { announce, LiveRegion };
}

/**
 * Screen reader utilities
 */

// Generate descriptive labels for complex UI elements
export function generateAriaLabel(context: {
  type: string;
  name?: string;
  status?: string;
  count?: number;
  position?: { current: number; total: number };
}): string {
  const { type, name, status, count, position } = context;
  
  let label = type;
  
  if (name) {
    label += ` ${name}`;
  }
  
  if (status) {
    label += `, status: ${status}`;
  }
  
  if (count !== undefined) {
    label += `, ${count} items`;
  }
  
  if (position) {
    label += `, item ${position.current} of ${position.total}`;
  }
  
  return label;
}

// Hook for managing expanded/collapsed state with ARIA
export function useAriaExpandable(initialExpanded: boolean = false) {
  const [isExpanded, setIsExpanded] = React.useState(initialExpanded);
  const triggerId = useAriaId('trigger');
  const contentId = useAriaId('content');

  const triggerProps = {
    'aria-expanded': isExpanded,
    'aria-controls': contentId,
    id: triggerId,
  };

  const contentProps = {
    'aria-labelledby': triggerId,
    id: contentId,
    hidden: !isExpanded,
  };

  return {
    isExpanded,
    setIsExpanded,
    triggerProps,
    contentProps,
  };
}

/**
 * Color contrast and theme utilities
 */

// Check if current theme is high contrast
export function useHighContrastMode() {
  const [isHighContrast, setIsHighContrast] = React.useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    setIsHighContrast(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsHighContrast(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isHighContrast;
}

// Check for reduced motion preference
export function useReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
}

/**
 * Form accessibility utilities
 */

// Hook for managing form field accessibility
export function useFormFieldAccessibility(options: {
  label: string;
  description?: string;
  error?: string;
  required?: boolean;
}) {
  const fieldId = useAriaId('field');
  const descriptionId = useAriaId('description');
  const errorId = useAriaId('error');

  const fieldProps = {
    id: fieldId,
    'aria-describedby': [
      options.description ? descriptionId : '',
      options.error ? errorId : '',
    ].filter(Boolean).join(' ') || undefined,
    'aria-invalid': !!options.error,
    'aria-required': options.required,
  };

  const labelProps = {
    htmlFor: fieldId,
  };

  const descriptionProps = options.description ? {
    id: descriptionId,
  } : {};

  const errorProps = options.error ? {
    id: errorId,
    role: 'alert',
    'aria-live': 'polite',
  } : {};

  return {
    fieldProps,
    labelProps,
    descriptionProps,
    errorProps,
  };
}

/**
 * Skip link utilities
 */

export function generateSkipLinks() {
  return [
    { href: '#main-content', label: 'Skip to main content' },
    { href: '#navigation', label: 'Skip to navigation' },
    { href: '#search', label: 'Skip to search' },
    { href: '#footer', label: 'Skip to footer' },
  ];
}

/**
 * Table accessibility utilities
 */

export function useTableAccessibility(data: any[], columns: string[]) {
  const tableId = useAriaId('table');
  const captionId = useAriaId('caption');

  const tableProps = {
    id: tableId,
    role: 'table',
    'aria-labelledby': captionId,
    'aria-rowcount': data.length + 1, // +1 for header
    'aria-colcount': columns.length,
  };

  const captionProps = {
    id: captionId,
  };

  const getRowProps = (rowIndex: number) => ({
    role: 'row',
    'aria-rowindex': rowIndex + 2, // +2 because header is row 1
  });

  const getCellProps = (colIndex: number, isHeader: boolean = false) => ({
    role: isHeader ? 'columnheader' : 'cell',
    'aria-colindex': colIndex + 1,
    scope: isHeader ? 'col' : undefined,
  });

  return {
    tableProps,
    captionProps,
    getRowProps,
    getCellProps,
  };
}

/**
 * Menu accessibility utilities
 */

export function useMenuAccessibility() {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerId = useAriaId('menu-trigger');
  const menuId = useAriaId('menu');

  const triggerProps = {
    id: triggerId,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? menuId : undefined,
    'aria-haspopup': 'menu' as const,
  };

  const menuProps = {
    id: menuId,
    role: 'menu',
    'aria-labelledby': triggerId,
    hidden: !isOpen,
  };

  const getMenuItemProps = (onClick?: () => void) => ({
    role: 'menuitem',
    tabIndex: -1,
    onClick: () => {
      onClick?.();
      setIsOpen(false);
    },
  });

  return {
    isOpen,
    setIsOpen,
    triggerProps,
    menuProps,
    getMenuItemProps,
  };
}

